import { db } from '../Services/Firebase/FirebaseIndex';
import { doc, getDoc, collection, updateDoc } from 'firebase/firestore';
import { hasThreeLevelHierarchy } from '../Pages/BrowseTalent/constants/talentCategories';
import { flattenVault } from './vaultNormalizer';
import { resolveName } from './resolveName';
import debug from './debug';

// Tier 2 — a talent profile becomes VISIBLE on Browse once these are complete.
// (Previously gated on all 8 sections; booking-only sections no longer block discovery.)
export const TALENT_VISIBILITY_COLLECTIONS = ['TalentBlueprint', 'TalentStudio', 'PerformanceHub'];

// Tier 3 — a talent is BOOKABLE once they're visible AND have pricing + compliance.
// Riders & crew are opt-in with safe defaults (later phases) and do NOT gate booking.
export const TALENT_BOOKING_COLLECTIONS = [...TALENT_VISIBILITY_COLLECTIONS, 'PricingPortfolio', 'ComplianceVault'];

const allComplete = async (userId, collections) => {
  const results = await Promise.all(
    collections.map((name) => checkMinDataCompleteFromCollection(userId, name))
  );
  return results.every((r) => r === true);
};

// Visibility gate (Tier 2) — drives Users.profile.profileActive.
export const checkTalentProfileActivation = async (userId) => {
  if (!userId) return false;
  try {
    const isActivated = await allComplete(userId, TALENT_VISIBILITY_COLLECTIONS);
    debug.log('ProfileActivation', 'Talent profile visible (activated):', isActivated);
    return isActivated;
  } catch (error) {
    debug.error('ProfileActivation', 'Error checking talent profile activation:', error);
    return false;
  }
};

// Booking-ready gate (Tier 3) — drives Users.profile.bookingReady.
// Level 2 = all five section flags PLUS phone number and full bio (which are
// bookable requirements, not live requirements). Must match the server sweep
// in functions/minDataRules.js (computeTalentFlags).
export const checkTalentBookingReady = async (userId) => {
  if (!userId) return false;
  try {
    const sectionsReady = await allComplete(userId, TALENT_BOOKING_COLLECTIONS);
    if (!sectionsReady) return false;

    const [bpSnap, stSnap] = await Promise.all([
      getDoc(doc(db, 'TalentBlueprint', userId)),
      getDoc(doc(db, 'TalentStudio', userId)),
    ]);
    const bp = bpSnap.exists() ? bpSnap.data() : null;
    const st = stSnap.exists() ? stSnap.data() : null;
    const contact = bp?.personalInfo?.contactNumber;
    const hasContact = typeof contact === 'string' ? contact.trim().length > 0 : !!contact;
    const hasFullBio = typeof st?.bio?.fullBio === 'string' && st.bio.fullBio.trim().length > 0;

    const isReady = hasContact && hasFullBio;
    debug.log('ProfileActivation', 'Talent booking-ready:', { sectionsReady, hasContact, hasFullBio, isReady });
    return isReady;
  } catch (error) {
    debug.error('ProfileActivation', 'Error checking talent booking readiness:', error);
    return false;
  }
};

export const checkOrganiserProfileActivation = async (userId) => {
  if (!userId) return false;

  try {
    debug.log('ProfileActivation', 'Checking organiser profile activation for user:', userId);

    // Check minDataComplete fields in separate UserRegistration collections
    const checks = {
      organiserBlueprint: await checkMinDataCompleteFromCollection(userId, 'OrganiserBlueprint'),
      complianceVault: await checkMinDataCompleteFromCollection(userId, 'ComplianceVault')
    };

    debug.log('ProfileActivation', 'Organiser activation checks:', checks);

    // All checks must pass for activation
    const isActivated = Object.values(checks).every(check => check === true);

    debug.log('ProfileActivation', 'Organiser profile activated:', isActivated);
    return isActivated;

  } catch (error) {
    debug.error('ProfileActivation', 'Error checking organiser profile activation:', error);
    return false;
  }
};

// Check minDataComplete field directly from individual collections
const checkMinDataCompleteFromCollection = async (userId, collectionName) => {
  try {
    debug.log('ProfileActivation', `Checking ${collectionName} collection for user:`, userId);

    const docRef = doc(db, collectionName, userId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      debug.log('ProfileActivation', `${collectionName} document does not exist for user:`, userId);
      return false;
    }

    const data = docSnap.data();
    const minDataComplete = data.minDataComplete;

    debug.log('ProfileActivation', `${collectionName} minDataComplete:`, minDataComplete);

    return minDataComplete === true;
  } catch (error) {
    debug.error('ProfileActivation', `Error checking ${collectionName}:`, error);
    return false;
  }
};

// Update profileActive field in Users.profile based on all minDataComplete statuses
export const updateProfileActiveStatus = async (userId, userType) => {
  if (!userId || !userType) return false;

  try {
    debug.log('ProfileActivation', 'Updating profileActive status for user:', userId, userType);

    let isActivated = false;
    let talentBookingReady = false;
    const updates = {};

    if (userType === 'Talent') {
      isActivated = await checkTalentProfileActivation(userId);
      // Track Tier 3 booking readiness alongside visibility for talent.
      talentBookingReady = await checkTalentBookingReady(userId);
      updates['profile.bookingReady'] = talentBookingReady;
      updates['profile.bookingReadyLastUpdated'] = new Date();
    } else if (userType === 'Organiser') {
      isActivated = await checkOrganiserProfileActivation(userId);
    }

    updates['profile.profileActive'] = isActivated;
    updates['profile.profileActiveLastUpdated'] = new Date();

    // Update the Users.profile activation fields
    const userRef = doc(db, 'Users', userId);
    await updateDoc(userRef, updates);

    debug.log('ProfileActivation', `Updated profileActive=${isActivated} for user:`, userId);

    // Close the loop: if this talent is now booking-ready, notify any organisers
    // who registered a request-to-book and mark those requests ready. Best-effort
    // and idempotent (only acts on `pending` requests). Dynamic import avoids a
    // load-time cycle with the notification stack.
    if (userType === 'Talent' && talentBookingReady) {
      try {
        const { notifyPendingRequestsOnReady } = await import('../Services/BookingRequests/bookingRequestService');
        await notifyPendingRequestsOnReady(userId);
      } catch (hookError) {
        debug.error('ProfileActivation', 'notifyPendingRequestsOnReady hook failed:', hookError);
      }
    }

    return isActivated;

  } catch (error) {
    debug.error('ProfileActivation', 'Error updating profileActive status:', error);
    return false;
  }
};

// Optimized profile activation check using Users.profile.profileActive
export const checkProfileActivationOptimized = async (userId, userType) => {
  if (!userId || !userType) return false;

  try {
    debug.log('ProfileActivation', 'Checking optimized profile activation for user:', userId);

    const userRef = doc(db, 'Users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      debug.log('ProfileActivation', 'User document does not exist:', userId);
      return false;
    }

    const userData = userSnap.data();
    const profileActive = userData.profile?.profileActive;

    debug.log('ProfileActivation', `Users.profile.profileActive:`, profileActive);

    return profileActive === true;

  } catch (error) {
    debug.error('ProfileActivation', 'Error checking optimized profile activation:', error);
    return false;
  }
};

// Get detailed activation status for UI display
export const getProfileActivationDetails = async (userId, userType) => {
  if (!userId || !userType) return null;

  try {
    if (userType === 'Talent') {
      // Visibility (Tier 2) — what the activation banner/badge report on.
      const checks = {
        talentBlueprint: await checkMinDataCompleteFromCollection(userId, 'TalentBlueprint'),
        talentStudio: await checkMinDataCompleteFromCollection(userId, 'TalentStudio'),
        performanceHub: await checkMinDataCompleteFromCollection(userId, 'PerformanceHub')
      };

      // Booking readiness (Tier 3) — extra fields for booking-prompt UI.
      const bookingChecks = {
        pricingPortfolio: await checkMinDataCompleteFromCollection(userId, 'PricingPortfolio'),
        complianceVault: await checkMinDataCompleteFromCollection(userId, 'ComplianceVault')
      };

      const completedCount = Object.values(checks).filter(Boolean).length;
      const totalCount = Object.keys(checks).length;
      const isActivated = completedCount === totalCount;

      return {
        isActivated,
        completedCount,
        totalCount,
        missingFields: Object.entries(checks)
          .filter(([_, isComplete]) => !isComplete)
          .map(([fieldName]) => fieldName),
        completedSections: Object.entries(checks)
          .filter(([_, isComplete]) => isComplete)
          .map(([fieldName]) => fieldName),
        checks,
        // Tier 3 — bookable once visible AND pricing + compliance are complete
        bookingReady: isActivated && Object.values(bookingChecks).every(Boolean),
        bookingChecks,
        bookingMissingFields: Object.entries(bookingChecks)
          .filter(([_, isComplete]) => !isComplete)
          .map(([fieldName]) => fieldName)
      };
    } else if (userType === 'Organiser') {
      const checks = {
        organiserBlueprint: await checkMinDataCompleteFromCollection(userId, 'OrganiserBlueprint'),
        complianceVault: await checkMinDataCompleteFromCollection(userId, 'ComplianceVault')
      };

      const completedCount = Object.values(checks).filter(Boolean).length;
      const totalCount = Object.keys(checks).length;

      return {
        isActivated: completedCount === totalCount,
        completedCount,
        totalCount,
        missingFields: Object.entries(checks)
          .filter(([_, isComplete]) => !isComplete)
          .map(([fieldName]) => fieldName),
        completedSections: Object.entries(checks)
          .filter(([_, isComplete]) => isComplete)
          .map(([fieldName]) => fieldName),
        checks
      };
    }

    return null;
  } catch (error) {
    debug.error('ProfileActivation', 'Error getting activation details:', error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Field-level completeness checklist (for the admin user lists).
//
// Returns, for a user, the Live Profile Status TIER plus a per-section, per-FIELD
// breakdown of what's been added vs what's still missing. The tier is derived
// from the authoritative section `minDataComplete` flags (the same flags that
// drive profileActive / bookingReady), while the field list mirrors each
// section's required-field rules for human-readable detail.
//
//   Talent:    incomplete → live (Level 1) → bookable (Level 2)
//   Organiser: incomplete → active
// ---------------------------------------------------------------------------

// Truthy-with-intent: non-empty string / non-empty array / non-empty object / true.
const _present = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return !!v;
};
// A document requirement is met by a persisted flag OR an uploaded file URL.
const _docOk = (...candidates) => candidates.some(_present);

export const getProfileFieldChecklist = async (userId, userType) => {
  if (!userId || !userType) return null;

  const readDoc = async (name) => {
    try {
      const snap = await getDoc(doc(db, name, userId));
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      return null;
    }
  };

  try {
    const userSnap = await getDoc(doc(db, 'Users', userId));
    const profile = userSnap.exists() ? (userSnap.data().profile || {}) : {};

    // Compliance fields are shared between talent & organiser; build once.
    // Shape-tolerant: read from a flattened view (flat root or nested bankDetails).
    const complianceFields = (rawCv) => {
      const cv = flattenVault(rawCv);
      const business = cv?.entityType === 'business'
        || (cv?.transactionType && cv.transactionType !== 'Individual');
      return [
        { label: 'ID number', present: _present(cv?.idNumber) },
        { label: 'Account holder', present: _present(cv?.accountHolder) },
        { label: 'Account number', present: _present(cv?.accountNumber) },
        { label: 'Account type', present: _present(cv?.accountType) },
        { label: 'Bank', present: _present(cv?.bank) },
        { label: 'Branch code', present: _present(cv?.branchCode) },
        { label: 'ID document', present: _docOk(cv?.idDocSubmitted, cv?.idDoc, cv?.idDocument) },
        { label: 'Bank confirmation letter', present: _docOk(cv?.bankConfirmLetterSubmitted, cv?.bankConfirmLetter, cv?.confirmationLetter) },
        ...(business
          ? [
              { label: 'Business address document', present: _docOk(cv?.businessAddressDocSubmitted, cv?.businessAddressDoc, cv?.businessAddressDocument) },
              { label: 'Business name', present: _present(cv?.businessName) },
              { label: 'Business reg number', present: _present(cv?.businessRegNumber) },
              { label: 'Business reg document', present: _docOk(cv?.businessRegUploaded, cv?.businessRegDoc, cv?.businessRegDocument) },
            ]
          : [
              { label: 'Proof of residence', present: _docOk(cv?.residenceDocSubmitted, cv?.residenceDoc, cv?.residenceDocument) },
            ]),
      ];
    };

    if (userType === 'Talent') {
      const [bp, st, ph, pr, cv] = await Promise.all(
        ['TalentBlueprint', 'TalentStudio', 'PerformanceHub', 'PricingPortfolio', 'ComplianceVault'].map(readDoc)
      );
      const needGenres = hasThreeLevelHierarchy(bp?.talentCategory);
      const pricingHasRate = Array.isArray(pr?.packages) && pr.packages.some(
        (p) => p && (p.duration === 'day_rate' ? _present(p.dayRateAmount) : _present(p.baseRate))
      );

      const l1 = bp?.minDataComplete === true && st?.minDataComplete === true && ph?.minDataComplete === true;
      // Phone number and full bio are Level-2 (bookable) requirements, not Level-1 (live).
      const hasContact = _present(bp?.personalInfo?.contactNumber);
      const hasFullBio = _present(st?.bio?.fullBio);
      const l2 = l1 && hasContact && hasFullBio && pr?.minDataComplete === true && cv?.minDataComplete === true;
      const tier = l2 ? 'bookable' : l1 ? 'live' : 'incomplete';

      return {
        userType,
        tier,
        sections: [
          { key: 'TalentBlueprint', label: 'Profile Basics', level: 1, complete: bp?.minDataComplete === true, fields: [
            { label: 'First name', present: _present(resolveName({ profile, personalInfo: bp?.personalInfo, blueprint: bp }).firstName) },
            { label: 'Last name', present: _present(resolveName({ profile, personalInfo: bp?.personalInfo, blueprint: bp }).lastName) },
            { label: 'Talent category', present: _present(bp?.talentCategory) },
            { label: 'Performance type', present: _present(bp?.performanceType) },
            { label: 'Home location', present: _present(bp?.homeLocation) },
            { label: 'Terms accepted', present: _present(bp?.termsAcceptance) },
            { label: 'Performance agreement', present: _present(bp?.performanceAgreement) },
          ] },
          { key: 'TalentStudio', label: 'Talent Studio', level: 1, complete: st?.minDataComplete === true, fields: [
            { label: 'Short bio', present: _present(st?.bio?.shortBio) },
            { label: 'Primary photo', present: _present(st?.media?.primaryPhoto) },
          ] },
          { key: 'PerformanceHub', label: 'Performance Hub', level: 1, complete: ph?.minDataComplete === true, fields: [
            { label: 'Event types', present: _present(ph?.eventTypes) },
            { label: 'Service areas', present: _present(ph?.serviceAreas) },
          ] },
          { key: 'PricingPortfolio', label: 'Pricing & Portfolio', level: 2, complete: pr?.minDataComplete === true, fields: [
            { label: 'At least one priced package', present: pricingHasRate },
          ] },
          { key: 'ComplianceVault', label: 'Compliance Vault', level: 2, complete: cv?.minDataComplete === true, fields: complianceFields(cv) },
        ],
      };
    }

    if (userType === 'Organiser') {
      const [bp, cv] = await Promise.all(['OrganiserBlueprint', 'ComplianceVault'].map(readDoc));
      const active = bp?.minDataComplete === true && cv?.minDataComplete === true;
      return {
        userType,
        tier: active ? 'active' : 'incomplete',
        sections: [
          { key: 'OrganiserBlueprint', label: 'Organiser Profile', level: 1, complete: bp?.minDataComplete === true, fields: [
            { label: 'Business name', present: _present(bp?.businessName) },
            { label: 'First name', present: _present(resolveName({ profile, blueprint: bp }).firstName) },
            { label: 'Last name', present: _present(resolveName({ profile, blueprint: bp }).lastName) },
            { label: 'Contact number', present: _present(bp?.contactNumber) },
            { label: 'Event types', present: _present(bp?.eventType) },
            { label: 'Location', present: _present(bp?.homeLocation) },
            { label: 'Terms accepted', present: _present(bp?.termsAcceptance) },
            { label: 'Performance agreement', present: _present(bp?.performanceAgreement) },
          ] },
          { key: 'ComplianceVault', label: 'Compliance Vault', level: 2, complete: cv?.minDataComplete === true, fields: complianceFields(cv) },
        ],
      };
    }

    return null;
  } catch (error) {
    debug.error('ProfileActivation', 'Error building field checklist:', error);
    return null;
  }
};